// ─── Business simulator helpers (Sep 2026) ────────────────────────────────
// Turns UI state (a "mix" of firm plans + config) into the pre-resolved items
// the engine expects, and orchestrates the mix optimizer: screen every
// combination of the candidate plans with few sims (same seed for all → the
// ranking compares rules, not luck), then re-validate the best ones with more
// sims and a weaker-edge stress test.
import {
  FIRM_DATABASE, resolveFundedRules, evalCosts, firmGroupOf, groupCap, groupLabel,
} from "../firmDatabase";
import { mcCall } from "./mcClient";

export const BIZ_DEFAULT_CONFIG = {
  bankroll: 1500,
  horizonMonths: 12,
  reinvestPct: 0.8,
  parallelAt: 4000,
  startSlots: 3,
  cushion: 100,
  dayCap: 0,
  correlation: "copy",
  useResets: true,
  promos: false,
  vat: false,
};

export const BIZ_DEFAULT_NSIMS = 1000;
export const BIZ_MAX_MIX = 8;

// Ready-made mixes. They only describe WHICH accounts to combine — results
// always come from the user's own strategy.
export const BIZ_PRESETS = [
  { id: "diversified", mix: [
    { firmId: "tradeify_select", planId: "tradeify_s_150k" },
    { firmId: "lucid_flex",      planId: "lucid_flex_50k" },
    { firmId: "apex_eod",        planId: "apex_eod_50k" },
    { firmId: "tpt",             planId: "tpt_25k" },
  ] },
  { id: "apex_only", mix: [
    { firmId: "apex_eod", planId: "apex_eod_50k" },
  ] },
  { id: "cheap_entry", mix: [
    { firmId: "tradeify_select", planId: "tradeify_s_25k" },
    { firmId: "lucid_flex",      planId: "lucid_flex_25k" },
    { firmId: "apex_eod",        planId: "apex_eod_25k" },
  ] },
];

export function findFirmPlan(firmId, planId) {
  const firm = FIRM_DATABASE.find(f => f.id === firmId);
  const plan = firm?.plans.find(p => p.planId === planId);
  return firm && plan ? { firm, plan } : null;
}

export function mixKey(m) { return `${m.firmId}:${m.planId}`; }

export function mixLabel(m) {
  const fp = findFirmPlan(m.firmId, m.planId);
  return fp ? `${groupLabel(firmGroupOf(fp.firm), fp.firm)} ${fp.plan.label}` : m.planId;
}

// Short code for charts/tables, e.g. "AP50", "TF150", "TP25".
export function mixShort(m) {
  const fp = findFirmPlan(m.firmId, m.planId);
  if (!fp) return m.planId;
  const g = firmGroupOf(fp.firm);
  const pre = { apex: "AP", tradeify: "TF", lucid: "LU", takeprofit: "TP", topstep: "TS" }[g]
    || fp.firm.name.replace(/[^A-Z]/g, "").slice(0, 2) || "X";
  return `${pre}${Math.round(fp.plan.capital / 1000)}`;
}

export function buildItems(mix, config) {
  return mix.map((m) => {
    const fp = findFirmPlan(m.firmId, m.planId);
    if (!fp) return null;
    const { firm, plan } = fp;
    const costs = evalCosts(plan, firm, { promos: config.promos, vat: config.vat });
    const fundedRules = resolveFundedRules(plan, firm.id);
    if (costs.vatMul !== 1 && fundedRules.fundedResetFee) {
      fundedRules.fundedResetFee *= costs.vatMul;
    }
    return {
      key: mixKey(m),
      label: mixLabel(m),
      group: firmGroupOf(firm),
      plan,
      fundedRules,
      fee: costs.fee,
      activation: costs.activation,
      monthly: plan.feeType === "monthly",
      sizeMult: Math.max(0.1, Number(m.sizeMult) || 1),
    };
  }).filter(Boolean);
}

// Groups present in a mix with their (possibly user-edited) caps.
export function mixGroups(mix, capsOverride = {}) {
  const seen = new Map();
  for (const m of mix) {
    const fp = findFirmPlan(m.firmId, m.planId);
    if (!fp) continue;
    const g = firmGroupOf(fp.firm);
    if (!seen.has(g)) {
      seen.set(g, { group: g, label: groupLabel(g, fp.firm), cap: capsOverride[g] ?? groupCap(g) });
    }
  }
  return [...seen.values()];
}

export function engineConfig(config, groupCaps, extra = {}) {
  return {
    bankroll: Math.max(50, Number(config.bankroll) || 0),
    horizonDays: Math.round(Math.max(1, Math.min(24, config.horizonMonths || 12)) * 21),
    reinvestPct: Math.max(0, Math.min(1, config.reinvestPct ?? 0.8)),
    parallelAt: Math.max(0, Number(config.parallelAt) || 0),
    startSlots: Math.max(1, Math.round(config.startSlots || 1)),
    cushion: Math.max(0, Number(config.cushion) || 0),
    dayCap: Math.max(0, Number(config.dayCap) || 0),
    correlation: config.correlation === "independent" ? "independent" : "copy",
    useResets: config.useResets !== false,
    groupCaps,
    ...extra,
  };
}

export function capsForMix(mix, capsOverride) {
  const caps = {};
  for (const g of mixGroups(mix, capsOverride)) caps[g.group] = g.cap;
  return caps;
}

export async function runBusiness(mix, strategy, config, capsOverride, nSims, seed) {
  const items = buildItems(mix, config);
  const cfg = engineConfig(config, capsForMix(mix, capsOverride));
  return mcCall("business", items, strategy, cfg, nSims, { seed });
}

// Robustness: same mix with a weaker edge (−10% / −25% of the mean daily pnl).
export async function runBusinessRobustness(mix, strategy, config, capsOverride, nSims, seed) {
  const items = buildItems(mix, config);
  const caps = capsForMix(mix, capsOverride);
  const out = [];
  for (const h of [0.10, 0.25]) {
    const r = await mcCall("business", items, strategy,
      engineConfig(config, caps, { edgeHaircut: h, lite: true }), nSims, { seed });
    out.push({ haircut: h, ...r });
  }
  return out;
}

// ─── Optimizer ────────────────────────────────────────────────────────────
export function subsetsUpTo(arr, maxK) {
  const out = [];
  const rec = (start, cur) => {
    if (cur.length) out.push(cur.slice());
    if (cur.length === maxK) return;
    for (let i = start; i < arr.length; i++) { cur.push(arr[i]); rec(i + 1, cur); cur.pop(); }
  };
  rec(0, []);
  return out;
}

// Pareto front on (low ruin, high median profit).
export function paretoFront(rows) {
  const sorted = [...rows].sort((a, b) => a.pRuin - b.pRuin || b.profitP50 - a.profitP50);
  const front = [];
  let best = -Infinity;
  for (const r of sorted) {
    if (r.profitP50 > best) { front.push(r.id); best = r.profitP50; }
  }
  return new Set(front);
}

// Rank: configs within the ruin limit first (by median profit), then the rest.
export function rankRows(rows, maxRuin) {
  return [...rows].sort((a, b) => {
    const okA = a.pRuin <= maxRuin, okB = b.pRuin <= maxRuin;
    if (okA !== okB) return okA ? -1 : 1;
    return okA ? b.profitP50 - a.profitP50 : a.pRuin - b.pRuin;
  });
}

export async function runOptimizer({
  candidates, strategy, config, capsOverride,
  maxK = 3, screenSims = 300, validateSims = 1500, topN = 5, maxRuin = 0.02,
  seed = 20260902, onProgress = () => {}, isCancelled = () => false,
}) {
  const subsets = subsetsUpTo(candidates, maxK);
  const total = subsets.length + topN * 2;
  let done = 0;
  const rows = [];
  for (const sub of subsets) {
    if (isCancelled()) return null;
    const r = await runBusiness(sub, strategy, { ...config }, capsOverride, screenSims, seed)
      .catch(() => null);
    done++;
    if (r) {
      rows.push({
        id: sub.map(mixKey).join("+"),
        mix: sub,
        short: sub.map(mixShort).join("+"),
        pRuin: r.pRuin, profitP50: r.profit.p50, profitP10: r.profit.p10,
        firstPayout: r.firstPayoutMedian, peak: r.peakFundedMedian, nSims: r.nSims,
      });
    }
    onProgress({ done, total, phase: "screen" });
  }
  const ranked = rankRows(rows, maxRuin);
  const finalists = ranked.slice(0, topN);
  const validated = [];
  for (const f of finalists) {
    if (isCancelled()) return null;
    const full = await runBusiness(f.mix, strategy, config, capsOverride, validateSims, seed + 1);
    done++;
    onProgress({ done, total, phase: "validate" });
    const stress = await mcCall("business", buildItems(f.mix, config), strategy,
      engineConfig(config, capsForMix(f.mix, capsOverride), { edgeHaircut: 0.10, lite: true }),
      Math.round(validateSims / 2), { seed: seed + 2 });
    done++;
    onProgress({ done, total, phase: "validate" });
    validated.push({
      ...f,
      pRuin: full.pRuin, pRuinCI: full.pRuinCI, profitP50: full.profit.p50, profitP10: full.profit.p10,
      firstPayout: full.firstPayoutMedian, peak: full.peakFundedMedian, nSims: full.nSims,
      stressP50: stress.profit.p50, stressRuin: stress.pRuin,
    });
  }
  const front = paretoFront(rows);
  return {
    rows: rows.map(r => ({ ...r, onFront: front.has(r.id) })),
    finalists: rankRows(validated, maxRuin),
    nConfigs: rows.length, screenSims, validateSims, maxRuin, seed,
  };
}
